import { DataSet, DataSetInformation, Record } from '@mtna/data-core-ui';
import { Variable, VariableSummary } from '@mtna/pojo-consumer-ui';

import { SelectParameters } from '../models/select-parameters.model';
import { VariableSummariesParameters } from '../models/variable-summaries-parameters.model';

const RDS_TABLE_MAX_ROWS = 21;
const RDS_TABLE_MAX_COLUMNS = 21;

const RDS_GHOST_RECORD: Record = {
  data: [],
  unit: {
    data: null,
  },
};

const RDS_GHOST_RESULT: DataSet = {
  uri: 'RDS_GHOST_DATA_SET',
  dataSetInformation: {
    columnCount: 0,
  },
  classifications: null,
  name: '',
  recordLayout: {
    variables: [],
  },
  recordUnit: null,
  totals: null,
  records: [],
};

export class RdsGhostVariable implements Variable {
  static count = 0;
  uri: string;
  dataType = 'GHOST';
  constructor() {
    this.uri = String(RdsGhostVariable.count);
    RdsGhostVariable.count++;
  }
}

export class RdsGhostVariableSummary implements VariableSummary {
  static count = 0;
  dataType = 'GHOST';
  id: string;
  index: number;
  label = '';
  name = '';
  uri: string;
  constructor() {
    this.uri = String(RdsGhostVariableSummary.count);
    this.id = this.uri;
    this.index = RdsGhostVariableSummary.count;
    RdsGhostVariableSummary.count++;
  }
}

/**
 * Generate ghost search results to show while loading.
 *
 * @param newParams New search parameters
 * @param lastParams Last search parameters
 * @param columnCount Total number of columns
 * @param rowCount Total number of rows
 * @returns Ghost DataSet
 */
export function rdsGenerateGhostResults(
  newParams: SelectParameters,
  lastParams: SelectParameters,
  columnCount: number,
  rowCount: number
): DataSet {
  const params = { ...lastParams, ...newParams };
  const dataSetInformation: DataSetInformation = {
    ...RDS_GHOST_RESULT.dataSetInformation,
    columnLimit: params.collimit,
    columnOffset: params.coloffset,
    rowLimit: params.limit,
    rowOffset: params.offset,
  };
  // Create fake number of variables equal to columns requested
  const variables: Array<Variable> = [];
  // Determine number of columns requested
  const numCols = !!columnCount
    ? Math.min(columnCount - params.coloffset, params.collimit, RDS_TABLE_MAX_COLUMNS)
    : Math.min(params.collimit, RDS_TABLE_MAX_COLUMNS);
  for (let i = 0; i < numCols; i++) {
    variables.push(new RdsGhostVariable());
  }

  // Create fake number of records equal to rows requested
  const records: Array<Record> = [];
  // Determine number of rows requested
  const numRows = !!rowCount
    ? Math.min(rowCount - params.offset, params.limit, RDS_TABLE_MAX_ROWS)
    : Math.min(params.limit, RDS_TABLE_MAX_ROWS);
  for (let i = 0; i < numRows; i++) {
    records.push(RDS_GHOST_RECORD);
  }
  return { ...RDS_GHOST_RESULT, dataSetInformation, recordLayout: { variables: variables }, records };
}

/**
 * Generate ghost variable summaries
 *
 * @param newParams New search parameters
 * @param lastParams Last search parameters
 * @param columnCount Total number of columns
 * @param rowCount Total number of rows
 * @returns Ghost DataSet
 */
export function rdsGenerateGhostVariableSummaries(
  newParams: VariableSummariesParameters,
  lastParams: VariableSummariesParameters,
  columnCount: number
): Array<VariableSummary> {
  const params = { ...lastParams, ...newParams };
  // Create fake number of variables equal to columns requested
  const variableSummaries: Array<VariableSummary> = [];
  // Determine number of columns requested
  const numCols = !!columnCount
    ? Math.min(columnCount - params.coloffset, params.collimit, RDS_TABLE_MAX_COLUMNS)
    : Math.min(params.collimit, RDS_TABLE_MAX_COLUMNS);
  for (let i = 0; i < numCols; i++) {
    variableSummaries.push(new RdsGhostVariableSummary());
  }
  return variableSummaries;
}
